import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SharedNavigation from "@/components/SharedNavigation";
import AchievementsTab from "@/components/AchievementsTab";
import PresenceStatusPicker from "@/components/PresenceStatusPicker";
import CommunityActivityFeed from "@/components/CommunityActivityFeed";
import CommunityPulseWidget from "@/components/CommunityPulseWidget";
import BlockedUsersSection from "@/components/BlockedUsersSection";
import StripeConnectCard from "@/components/StripeConnectCard";

const Stat = ({ label, value, icon, accent = "purple", testid }) => (
  <div
    className={`rounded-xl bg-${accent}-500/10 border border-${accent}-500/30 px-3 py-3`}
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
      <span>{icon}</span>
      <span className="truncate">{label}</span>
    </div>
    <div className="text-2xl font-black text-white mt-0.5 truncate">
      {typeof value === "number" ? value.toLocaleString() : (value ?? "—")}
    </div>
  </div>
);

const QuickAction = ({ to, icon, label, testid, accent = "purple" }) => (
  <Link
    to={to}
    className={`group flex items-center gap-3 px-4 py-3 rounded-xl bg-${accent}-500/10 border border-${accent}-500/30 hover:bg-${accent}-500/20 transition`}
    data-testid={testid}
  >
    <span className="text-2xl flex-shrink-0">{icon}</span>
    <span className="font-semibold text-white group-hover:text-white">{label}</span>
  </Link>
);

const CreatorHubPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [hubStats, setHubStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        const u = me.data?.user || me.data;
        if (!u || !u.user_id) { navigate("/login?next=/creator-hub"); return; }
        setUser(u);
        // Parallel fetch (Phase 15.6P — hub-stats is the authoritative source for tiles)
        const [ach, wal, rwd, hub] = await Promise.all([
          api.get(`/users/${u.user_id}/achievements`).catch(() => null),
          api.get("/wallet/me").catch(() => null),
          api.get("/rewards/me?limit=10").catch(() => null),
          api.get("/users/me/hub-stats").catch(() => null),
        ]);
        if (ach?.data) setStats(ach.data);
        if (wal?.data) setWallet(wal.data);
        if (rwd?.data) setRewards(rwd.data);
        if (hub?.data) setHubStats(hub.data);
      } catch {
        navigate("/login?next=/creator-hub");
      }
    })();
  }, [navigate]);

  if (!user) return <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">Loading Creator Hub…</div>;

  // Phase 15.6P — Authoritative balance straight from /users/me/hub-stats
  // (wallet fallback only if the hub-stats call failed).
  const balance = hubStats?.coin_balance ?? ((wallet?.coin_balance ?? 0) + (wallet?.reward_balance ?? 0));

  return (
    <div className="min-h-screen bg-black" data-testid="creator-hub-page">
      <SharedNavigation currentUser={user} />
      <div className="pt-20 px-4 pb-12 max-w-6xl mx-auto">
        <header className="mb-6">
          <h1 className="text-4xl sm:text-5xl font-black text-white">🎛️ Creator Hub</h1>
          <p className="text-gray-400 text-sm mt-2 max-w-2xl">
            Your command center — overview, earnings, achievements, content, and quick actions in one place.
          </p>
        </header>

        {/* Phase 17.0 — Stripe Connect Express card */}
        <StripeConnectCard variant="hub" />

        {/* Phase 17.1 — Producer Dashboard quick link */}
        <Link
          to="/producer"
          className="block mb-4 rounded-xl border border-purple-500/30 bg-purple-500/5 hover:bg-purple-500/10 transition-colors px-4 py-3"
          data-testid="hub-producer-dashboard-link"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-bold text-purple-100">💎 Producer Dashboard · Beat Earnings</div>
              <div className="text-xs text-gray-400 mt-0.5">Manage your beats, view sales, and request beat payouts.</div>
            </div>
            <span className="text-purple-200 text-lg">→</span>
          </div>
        </Link>

        {/* Overview stats (Phase 15.6P — authoritative from /users/me/hub-stats) */}
        <section className="mb-6" data-testid="hub-overview">
          <h2 className="text-white font-bold text-base mb-3">📊 Overview</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat icon="▶" label="Total plays" value={hubStats?.total_plays ?? stats?.stats?.promotional_plays_total} accent="purple" testid="hub-stat-plays" />
            <Stat icon="🪙" label="Coin balance" value={balance} accent="amber" testid="hub-stat-coins" />
            <Stat icon="👥" label="Followers" value={hubStats?.follower_count ?? (user.followers?.length ?? 0)} accent="emerald" testid="hub-stat-followers" />
            <Stat icon="🎶" label="Tracks" value={hubStats?.track_count ?? (user.submission_count ?? 0)} accent="purple" testid="hub-stat-tracks" />
            <Stat icon="🏆" label="Spotlight wins" value={hubStats?.spotlight_wins ?? stats?.stats?.spotlight_wins_total} accent="amber" testid="hub-stat-spotlight" />
            <Stat icon="🎯" label="Referrals" value={hubStats?.referral_count ?? stats?.stats?.referrals_count} accent="emerald" testid="hub-stat-referrals" />
          </div>
        </section>

        {/* Phase 15.6P — Community Pulse Widget */}
        <section className="mb-6" data-testid="hub-community-pulse">
          <CommunityPulseWidget />
        </section>

        {/* Quick actions */}
        <section className="mb-6" data-testid="hub-quick-actions">
          <h2 className="text-white font-bold text-base mb-3">⚡ Quick actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <QuickAction to="/submit" icon="🎵" label="Submit new music" testid="hub-action-submit-music" accent="purple" />
            <QuickAction to="/submit-beat" icon="🎹" label="Upload beat" testid="hub-action-upload-beat" accent="purple" />
            <QuickAction to="/live" icon="🔴" label="Go live" testid="hub-action-go-live" accent="rose" />
            <QuickAction to={`/artist/${user.user_id}`} icon="👤" label="View public profile" testid="hub-action-public-profile" accent="emerald" />
            <QuickAction to="/wallet" icon="🪙" label="Open Wallet · Earnings" testid="hub-action-wallet" accent="amber" />
            <QuickAction to="/referrals/leaderboard" icon="🔗" label="Share referral link" testid="hub-action-referrals" accent="purple" />
          </div>
        </section>

        {/* Presence picker */}
        <section className="mb-6" data-testid="hub-presence">
          <PresenceStatusPicker currentUser={user} />
        </section>

        {/* Wallet preview */}
        <section className="mb-6" data-testid="hub-wallet-preview">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-bold text-base">🪙 Wallet & Rewards</h2>
            <Link to="/wallet" className="text-purple-300 hover:text-purple-200 text-xs">Open full wallet →</Link>
          </div>
          <Card className="p-4 bg-gradient-to-br from-amber-500/10 to-yellow-500/5 border-amber-500/30">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-amber-200/70">Total balance</div>
                <div className="text-4xl font-black text-amber-200" data-testid="hub-wallet-balance">
                  {balance.toLocaleString()} 🪙
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-amber-200/70">All-time rewards</div>
                <div className="text-2xl font-bold text-amber-200">
                  +{(rewards?.grand_total_coins || 0).toLocaleString()}
                </div>
              </div>
            </div>
            {rewards?.rewards?.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-gray-300 border-t border-amber-500/20 pt-3" data-testid="hub-rewards-recent">
                {rewards.rewards.slice(0, 5).map((r, i) => (
                  <li key={i} className="flex items-center justify-between">
                    <span className="truncate">{r.title || r.reward_type}</span>
                    <span className="text-amber-300 font-semibold">+{r.amount}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </section>

        {/* Achievements */}
        <section className="mb-6" data-testid="hub-achievements">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-bold text-base">🏆 Achievements</h2>
            <Link to={`/artist/${user.user_id}?tab=achievements`} className="text-purple-300 hover:text-purple-200 text-xs">
              View on public profile →
            </Link>
          </div>
          <AchievementsTab userId={user.user_id} />
        </section>

        {/* Phase 15.6N — Community activity feed */}
        <section data-testid="hub-community-feed">
          <CommunityActivityFeed limit={10} compact />
        </section>

        {/* Phase 15.6O — Blocked Users (auto-hides when empty) */}
        <section data-testid="hub-blocked-users">
          <BlockedUsersSection />
        </section>

        {/* Quick links */}
        <section data-testid="hub-shortcuts">
          <h2 className="text-white font-bold text-base mb-3">🧭 Quick links</h2>
          <div className="flex flex-wrap gap-2">
            <Link to="/home"><Button variant="outline" className="border-purple-500/40 text-purple-200" data-testid="hub-link-home">🏠 Home</Button></Link>
            <Link to="/dashboard"><Button variant="outline" className="border-purple-500/40 text-purple-200" data-testid="hub-link-dashboard">🎛️ Dashboard</Button></Link>
            <Link to="/explore"><Button variant="outline" className="border-purple-500/40 text-purple-200" data-testid="hub-link-explore">🔍 Explore</Button></Link>
            <Link to="/winners"><Button variant="outline" className="border-amber-500/40 text-amber-200" data-testid="hub-link-winners">🏆 Winners</Button></Link>
            <Link to="/reports"><Button variant="outline" className="border-purple-500/40 text-purple-200" data-testid="hub-link-reports">📰 RGM Reports</Button></Link>
            <Link to="/crews"><Button variant="outline" className="border-purple-500/40 text-purple-200" data-testid="hub-link-crews">🤝 Crews</Button></Link>
            <Link to="/beams"><Button variant="outline" className="border-purple-500/40 text-purple-200" data-testid="hub-link-beams">🎙️ Beams</Button></Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CreatorHubPage;
